/**
 * @zendir/ui - ImagePanel Component
 *
 * Reusable chrome around `<ImageFrame>` for sensor/CCD-style image
 * surfaces. Adds the bits every imaging panel needs but `<ImageFrame>`
 * intentionally doesn't:
 *
 *   - **Refresh button** with disabled / busy states.
 *   - **Error banner** when the upstream capture failed.
 *   - **Title strip + frame** in a single bordered card.
 *
 * Like `<ImageFrame>`, this component is **purely presentational** —
 * it knows nothing about where the data came from. Callers wire up the
 * I/O (engine, file, websocket) and feed:
 *
 *   - `data` / `pixels` / `width` / `maxADU` / `colormap` → forwarded
 *     to `<ImageFrame>` unchanged.
 *   - `onRefresh` → click handler for the button.
 *   - `loading` → toggles the button's "Capturing…" label AND the
 *     frame's skeleton state when there's no frame yet.
 *   - `error` → renders a red strip above the frame.
 *
 * The component handles the common refresh-disabled-while-loading
 * pattern internally so callers don't have to compute it.
 *
 * @example
 * ```tsx
 * <ImagePanel
 *   title="CCD capture"
 *   data={samples}
 *   width={28}
 *   maxADU={65535}
 *   loading={busy}
 *   error={err}
 *   onRefresh={capture}
 *   refreshDisabled={!ccd}
 *   emptyMessage="No frame captured yet."
 * />
 * ```
 */

import React, { memo } from "react";
import { useTheme } from "../../theme";
import { classNames } from "../../utils";
import { ImageFrame, type ImageColormap } from "./ImageFrame";

// =============================================================================
// Types
// =============================================================================

export interface ImagePanelProps {
  // -- Frame inputs (forwarded to <ImageFrame>) ----------------------------
  /** Flat, row-major single-channel samples. Length must equal width*height. */
  data?: number[];
  /** Pre-decoded RGBA pixels — escape hatch for callers with their own buffer. */
  pixels?: Uint8ClampedArray;
  /** Frame width in pixels. */
  width: number;
  /** Frame height in pixels. Defaults to `width` (square). */
  height?: number;
  /** Saturation ceiling used to normalise `data` → [0,1]. Default 65535. */
  maxADU?: number;
  /** Colormap applied to normalised intensities. Default 'cividis'. */
  colormap?: ImageColormap;
  /** Show the raw sample values as a grid below the image. Default false. */
  showValues?: boolean;
  /** Message shown when no frame is available. */
  emptyMessage?: string;

  // -- Panel chrome --------------------------------------------------------
  /** Title rendered above the frame. */
  title?: string;
  /** True while a capture is in flight. Drives the button label + skeleton. */
  loading?: boolean;
  /** Error from the upstream capture. Rendered as a red banner above the frame. */
  error?: string | null;
  /** Click handler for the refresh button. Omit to hide the button entirely. */
  onRefresh?: () => void;
  /** Label for the idle refresh button. Default 'Refresh'. */
  refreshLabel?: string;
  /** Label while `loading` is true. Default 'Capturing…'. */
  refreshingLabel?: string;
  /**
   * Disable the refresh button even when not loading — e.g. when the
   * caller has no data source bound (no CCD selected). The button is
   * always disabled while `loading` is true regardless of this prop.
   */
  refreshDisabled?: boolean;
  /** Extra class name applied to the outer wrapper. */
  className?: string;
}

// =============================================================================
// Component
// =============================================================================

export const ImagePanel = memo(function ImagePanel({
  // frame
  data,
  pixels,
  width,
  height,
  maxADU,
  colormap,
  showValues,
  emptyMessage,
  // chrome
  title,
  loading = false,
  error = null,
  onRefresh,
  refreshLabel = "Refresh",
  refreshingLabel = "Capturing…",
  refreshDisabled = false,
  className = "",
}: ImagePanelProps): React.ReactElement {
  const { tokens } = useTheme();

  // Button is disabled while loading OR when caller explicitly disables it
  // (e.g. no source bound yet). Coalesced here so callers don't repeat the
  // `loading || refreshDisabled` pattern.
  const buttonDisabled = loading || refreshDisabled;

  // Skeleton state inside ImageFrame should fire only while loading AND
  // there's nothing to show yet — once we have data, repeat captures
  // refresh in-place without flashing the skeleton.
  const hasFrame = Array.isArray(data) || pixels instanceof Uint8ClampedArray;
  const frameLoading = loading && !hasFrame;

  return (
    <div
      data-testid="image-panel"
      className={classNames("zendir-image-panel", className)}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacing.sm,
        fontFamily: tokens.typography.fontFamily.primary,
      }}
    >
      {onRefresh && (
        <button
          type="button"
          data-testid="image-panel-refresh"
          onClick={onRefresh}
          disabled={buttonDisabled}
          aria-busy={loading ? "true" : "false"}
          style={{
            alignSelf: "flex-start",
            padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
            borderRadius: tokens.borderRadius.sm,
            border: `1px solid ${tokens.colors.border.muted}`,
            background: buttonDisabled
              ? "rgba(255,255,255,0.03)"
              : "rgba(255,255,255,0.08)",
            color: buttonDisabled
              ? tokens.colors.text.tertiary
              : tokens.colors.text.primary,
            cursor: buttonDisabled ? "default" : "pointer",
            fontSize: tokens.typography.fontSize.xs,
            fontFamily: tokens.typography.fontFamily.primary,
            // Make the disabled state visually consistent across browsers.
            opacity: buttonDisabled ? 0.85 : 1,
          }}
        >
          {loading ? refreshingLabel : refreshLabel}
        </button>
      )}

      {error && (
        <div
          data-testid="image-panel-error"
          role="alert"
          style={{
            color: tokens.colors.status.critical,
            fontSize: tokens.typography.fontSize.xs,
            background: `${tokens.colors.status.critical}14`,
            border: `1px solid ${tokens.colors.status.critical}33`,
            borderRadius: tokens.borderRadius.sm,
            padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
            wordBreak: "break-word",
          }}
        >
          {error}
        </div>
      )}

      <ImageFrame
        data={data}
        pixels={pixels}
        width={width}
        height={height}
        maxADU={maxADU}
        colormap={colormap}
        showValues={showValues}
        title={title}
        loading={frameLoading}
        emptyMessage={emptyMessage}
      />
    </div>
  );
});

export default ImagePanel;
